<template>
	<div
		ref="actionShortcutElementRef"
		class="resource-planning-actions-shortcut"
	>
		<VDropdown
			:distance="5"
			:shown="isShortcutOpen"
			:triggers="[]"
			popper-class="resource-planning-actions-shortcut__dropdown"
			@auto-hide="hideShortcutPopper"
		>
			<SuiButton
				class="resource-planning-actions-shortcut__dropdown-button"
				data-testid="resource-planning-actions-shortcut-button"
				variant="unstyled"
				@click="onDropdownButtonClick"
			>
				<SuiIcon
					name="icon-meatballs-16"
					class="resource-planning-actions-shortcut__icon"
				/>
			</SuiButton>

			<template #popper>
				<div class="resource-planning-actions-shortcut__action-list">
					<SuiButton
						v-for="(
							{ iconName, text, onClick }, index
						) in visibleActions"
						:key="text + index"
						variant="unstyled"
						class="resource-planning-actions-shortcut__action"
						@click="onClick"
					>
						<SuiIcon
							:name="iconName"
							class="resource-planning-actions-shortcut__icon"
						/>
						{{ text }}
					</SuiButton>
				</div>
			</template>
		</VDropdown>
	</div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { SuiIcon, SuiButton } from '@developers/scoro-ui';
import {
	closePopOver,
	openBookingModal,
	openConfirmationModal,
	openProjectMemberDropdown,
} from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { type ResourcePlanningGanttConfig } from '../ResourcePlanningGantt/ResourcePlanningGanttConfig';
import {
	type ActionShortcutOption,
	type ResourcePlanningItem,
} from '../../types';
import {
	ACTION_SHORTCUT_OPTIONS,
	DEFAULT_BOOKING_AMOUNT,
} from '../../constants';
import { type ResourcePlanningBookingModalProps } from '../ResourcePlanningBookingModal';
import { useTranslation } from '@/composables';
import {
	type BookingSaveInput,
	BookingAmountType,
	type User,
	TYPES,
	type RoleProject,
	type Project,
	type Role,
	type RetainerPeriod,
} from '@/generated/graphql';
import { resetTime } from '@/utils/formatting';
import { ALL_RETAINER_PERIODS_ID } from '@/features/Retainer';

export interface ResourcePlanningActionShortcutProps {
	options: ActionShortcutOption[];
	projectId: string;
	retainerPeriodId: string;
	projectRetainerPeriods?: Pick<
		RetainerPeriod,
		'id' | 'startDate' | 'endDate'
	>[];
	projectStartDate: Date;
	entityId: User['id'] | RoleProject['id'];
	entityType: typeof TYPES.User | typeof TYPES.RoleProject;
	hasTentativeBookingsFeature: boolean;
	resource?: ResourcePlanningItem;
	project?: ResourcePlanningItem;
	config: ResourcePlanningGanttConfig;
}

const props = defineProps<ResourcePlanningActionShortcutProps>();
const emit = defineEmits<{
	(
		event: 'project-member-removed',
		id: User['id'],
		projectId: Project['id']
	): void;
	(
		event: 'project-role-removed',
		id: RoleProject['id'],
		projectId: Project['id']
	): void;
	(
		event: 'move-bookings',
		toUserId: User['id'],
		fromEntityId: User['id'] | RoleProject['id'],
		fromEntityType: typeof TYPES.RoleProject | typeof TYPES.User,
		projectId: Project['id']
	): void;
	(
		event: 'clear-bookings',
		id: User['id'] | RoleProject['id'],
		entityType: typeof TYPES.RoleProject | typeof TYPES.User
	): void;
	(
		event: 'update-booking',
		input: BookingSaveInput,
		entityId: User['id'] | RoleProject['id'],
		entityType: typeof TYPES.RoleProject | typeof TYPES.User
	): void;
	(
		event: 'assign-bookings',
		toUserId: User['id'],
		fromRoleProjectId: RoleProject['id'],
		projectId: Project['id']
	): void;
}>();

const isShortcutOpen = ref(false);

const actionShortcutElementRef = ref<HTMLDivElement | null>(null);

const { t } = useTranslation();

const showNewBookingOption = computed(() =>
	props.options.some(
		(option) => option === ACTION_SHORTCUT_OPTIONS.NEW_BOOKING
	)
);
const showAssignMemberOption = computed(() =>
	props.options.some(
		(option) => option === ACTION_SHORTCUT_OPTIONS.ASSIGN_MEMBER
	)
);
const showMoveBookingsOption = computed(() =>
	props.options.some(
		(option) => option === ACTION_SHORTCUT_OPTIONS.MOVE_BOOKINGS
	)
);
const showClearBookingsOption = computed(() =>
	props.options.some(
		(option) => option === ACTION_SHORTCUT_OPTIONS.CLEAR_BOOKINGS
	)
);
const showRemovingUserOption = computed(() =>
	props.options.some(
		(option) => option === ACTION_SHORTCUT_OPTIONS.REMOVE_PROJECT_MEMBER
	)
);

const onDropdownButtonClick = () => {
	isShortcutOpen.value = !isShortcutOpen.value;

	closePopOver();
};

const hideShortcutPopper = () => {
	isShortcutOpen.value = false;
};

const isRetainerPeriodId = computed(() => {
	if (!props.retainerPeriodId) {
		return false;
	}
	const parsed = Number(props.retainerPeriodId);
	return !isNaN(parsed) && parsed !== ALL_RETAINER_PERIODS_ID;
});

const getRetainerStartDate = (retainerPeriodId: RetainerPeriod['id']) => {
	const period = props.projectRetainerPeriods?.find(
		(p) => p.id === retainerPeriodId
	);
	if (!period?.startDate) {
		return undefined;
	}
	return resetTime(period.startDate);
};

const getRetainerEndDate = (retainerPeriodId: RetainerPeriod['id']) => {
	const period = props.projectRetainerPeriods?.find(
		(p) => p.id === retainerPeriodId
	);
	if (!period?.endDate) {
		return undefined;
	}
	return resetTime(period.endDate);
};

const onNewBooking = () => {
	const options: ResourcePlanningBookingModalProps & {
		onUpdateBooking: (
			input: BookingSaveInput,
			entityId: User['id'] | RoleProject['id'],
			entityType: typeof TYPES.RoleProject | typeof TYPES.User
		) => void;
	} = {
		projectId: props.projectId,
		projectStartDate: isRetainerPeriodId.value
			? undefined
			: props.projectStartDate,
		selectedEntityId: props.entityId,
		selectedEntityType: props.entityType,
		selectedEntityName: props.resource?.text ?? '',
		amount: DEFAULT_BOOKING_AMOUNT.PERCENT,
		amountType: BookingAmountType.Percent,
		isTentative: props.entityType === TYPES.RoleProject,
		hasTentativeBookingsFeature: props.hasTentativeBookingsFeature,
		startDate: isRetainerPeriodId.value
			? getRetainerStartDate(props.retainerPeriodId)
			: undefined,
		endDate: isRetainerPeriodId.value
			? getRetainerEndDate(props.retainerPeriodId)
			: undefined,
		onUpdateBooking: (
			input: BookingSaveInput,
			entityId: User['id'] | RoleProject['id'],
			entityType: typeof TYPES.RoleProject | typeof TYPES.User
		) => {
			emit('update-booking', input, entityId, entityType);
		},
	};

	openBookingModal(null, options, {
		placement: 'top',
	});

	hideShortcutPopper();
};

const onMoveBookingUserOrRoleSelected = (
	id: User['id'] | RoleProject['id'],
	name: User['name'] | Role['name']
) => {
	openConfirmationModal({
		message: props.config.getMoveBookingsConfirmationLangVar(),
		messageValue: {
			project: props.project?.text ?? '',
			resource: name,
			name: props.resource?.text ?? '',
		},
		isInitiallyOpen: true,
		onConfirm: () =>
			emit(
				'move-bookings',
				id,
				props.entityId,
				props.entityType,
				props.projectId
			),
	});
};

const onMoveBookings = () => {
	if (actionShortcutElementRef.value) {
		openProjectMemberDropdown(
			actionShortcutElementRef.value,
			{
				title: t('resource-booking__move-bookings'),
				onUserSelected: onMoveBookingUserOrRoleSelected,
				onRoleSelected: onMoveBookingUserOrRoleSelected,
				excludeUserId:
					props.entityType === TYPES.User ? props.entityId : null,
				projectId: props.projectId,
				selectedEntityId: props.entityId,
				selectedEntityType: props.entityType,
			},
			{
				placement: 'bottom-end',
			}
		);
	}

	hideShortcutPopper();
};
const onAssignMemberUserSelected = (id: User['id'], name: User['name']) => {
	const roleProjectId = props.entityId;

	openConfirmationModal({
		message: 'resource_booking_assign_member_confirm',
		messageValue: { roleName: props.resource?.text ?? '', userName: name },
		isInitiallyOpen: true,
		onConfirm: () =>
			emit('assign-bookings', id, roleProjectId, props.projectId),
	});
};

const onAssignMember = () => {
	if (actionShortcutElementRef.value) {
		openProjectMemberDropdown(
			actionShortcutElementRef.value,
			{
				title: t('resource-booking__move-bookings'),
				projectId: props.projectId,
				selectedEntityId: props.entityId,
				selectedEntityType: props.entityType,
				excludeUserId: null,
				onUserSelected: onAssignMemberUserSelected,
			},
			{
				placement: 'bottom-end',
			}
		);
	}

	hideShortcutPopper();
};

const onClearBookings = () => {
	openConfirmationModal({
		message: props.config.getClearBookingsConfirmationLangVar(),
		messageValue: {
			project: props.project?.text ?? '',
			name: props.resource?.text ?? '',
		},
		isInitiallyOpen: true,
		onConfirm: () => {
			emit('clear-bookings', props.entityId, props.entityType);
		},
	});
	hideShortcutPopper();
};

const onRemoveMember = () => {
	const modalProps = {
		message: props.config.getRemoveProjectMemberConfirmationLangVar(),
		messageValue: {
			project: props.project?.text ?? '',
			name: props.resource?.text ?? '',
		},
		isInitiallyOpen: true,
		onConfirm: () => {
			if (props.entityType === TYPES.User) {
				emit('project-member-removed', props.entityId, props.projectId);
			} else {
				emit('project-role-removed', props.entityId, props.projectId);
			}
		},
	};

	openConfirmationModal(modalProps);
	hideShortcutPopper();
};

const visibleActions = computed(() =>
	[
		{
			isVisible: showNewBookingOption.value,
			iconName: 'icon-add-16',
			text: t('resource_planning_new_booking'),
			onClick: onNewBooking,
		},
		{
			isVisible: showAssignMemberOption.value,
			iconName: 'icon-addperson-16',
			text: t('resource_planning_assign_member'),
			onClick: onAssignMember,
		},
		{
			isVisible: showMoveBookingsOption.value,
			iconName: 'icon-longarrow-left-16',
			text: t('resource_planning_replace_project_member'),
			onClick: onMoveBookings,
		},
		{
			isVisible: showClearBookingsOption.value,
			iconName: 'icon-bin-16',
			text: t('resource_planning_clear_bookings'),
			onClick: onClearBookings,
		},
		{
			isVisible: showRemovingUserOption.value,
			iconName: 'icon-removeperson-16',
			text: t('resource_planning_remove_project_member'),
			onClick: onRemoveMember,
		},
	].filter(({ isVisible }) => isVisible)
);
</script>

<style lang="scss">
.resource-planning-actions-shortcut {
	&__dropdown {
		--popper-wrapper-min-width: auto;
	}
}
</style>

<style lang="scss" scoped>
.resource-planning-actions-shortcut {
	$parent: &;

	&__dropdown-button {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		padding: 1rem;
	}

	#{$parent}__icon {
		--sui-button-icon-color: #{$neutral-60};

		margin: 0;
	}

	&__dropdown {
		#{$parent}__action-list {
			padding: 1rem 0;

			--sui-button-icon-color: #{$neutral-60};
		}

		#{$parent}__action {
			--sui-button-text-color: #{$neutral-90};
			--sui-button-hover-background-color: #{$neutral-10};

			font-weight: $font-weight-normal;
			border-radius: 0;
			width: 100%;
			justify-content: flex-start;
			gap: 1rem;
			align-items: end;
		}

		#{$parent}__icon {
			--sui-button-icon-color: #{$neutral-60};

			margin: -0.25rem 0 0;
		}
	}
}
</style>
